import { IRREGULAR_LEMMAS } from "../content/lexicon/verbalore-entries";
import type { LessonLexiconEntry } from "../content/types";

type PackedDictionaryEntry = [
  lemma: string,
  pronunciations: Array<[value: string, variety?: string]>,
  senses: Array<[
    id: string,
    partOfSpeech: string,
    definition: string,
    examples: string[],
    synonyms: string[],
  ]>,
];

type DictionaryShard = {
  schemaVersion: "verbalore-private-dictionary-shard-v1";
  entries: Record<string, PackedDictionaryEntry>;
};

const shardCache = new Map<string, Promise<DictionaryShard | null>>();

export function normalizeBroadDictionaryWord(value: string) {
  return value
    .normalize("NFKC")
    .toLocaleLowerCase()
    .replace(/[’']/gu, "'")
    .replace(/^[^\p{L}]+|[^\p{L}]+$/gu, "")
    .trim();
}

export function broadDictionaryShard(value: string) {
  let hash = 0x811c9dc5;
  for (const character of value) {
    hash ^= character.codePointAt(0) || 0;
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash & 0xff).toString(16).padStart(2, "0");
}

export function broadDictionaryCandidates(value: string) {
  const word = normalizeBroadDictionaryWord(value);
  const candidates = new Set<string>();
  if (!word) return [];
  // Prefer the learner's exact selected word whenever it has its own lexical
  // record. Inflection recovery is a fallback, never permission to turn an
  // exact noun such as "news" into the different word "new".
  candidates.add(word);
  if (IRREGULAR_LEMMAS[word]) candidates.add(IRREGULAR_LEMMAS[word]);
  const add = (candidate: string) => candidate.length > 1 && candidates.add(candidate);
  if (word.endsWith("'s")) add(word.slice(0, -2));
  if (word.endsWith("ies") && word.length > 4) add(word.slice(0, -3) + "y");
  if (word.endsWith("ves") && word.length > 4) {
    add(word.slice(0, -3) + "f");
    add(word.slice(0, -3) + "fe");
  }
  if (word.endsWith("es") && word.length > 3) {
    add(word.slice(0, -2));
    add(word.slice(0, -1));
  }
  if (word.endsWith("s") && word.length > 3) add(word.slice(0, -1));
  if (word.endsWith("ied") && word.length > 4) add(word.slice(0, -3) + "y");
  if (word.endsWith("ed") && word.length > 3) {
    const stem = word.slice(0, -2);
    add(stem);
    add(stem + "e");
    if (stem.length > 2 && stem.at(-1) === stem.at(-2)) add(stem.slice(0, -1));
  }
  if (word.endsWith("ing") && word.length > 5) {
    const stem = word.slice(0, -3);
    add(stem);
    add(stem + "e");
    if (stem.length > 2 && stem.at(-1) === stem.at(-2)) add(stem.slice(0, -1));
  }
  if (word.endsWith("er") && word.length > 4) {
    add(word.slice(0, -2));
    add(word.slice(0, -1));
  }
  if (word.endsWith("est") && word.length > 5) {
    add(word.slice(0, -3));
    add(word.slice(0, -2));
  }
  return [...candidates];
}

export async function findBroadDictionaryEntry(value: string): Promise<LessonLexiconEntry | null> {
  for (const candidate of broadDictionaryCandidates(value)) {
    const shard = broadDictionaryShard(candidate);
    const pack = await loadShard(shard);
    const entry = pack?.entries[candidate];
    if (entry) return unpackEntry(entry);
  }
  return null;
}

function unpackEntry(entry: PackedDictionaryEntry): LessonLexiconEntry {
  return {
    lemma: entry[0],
    pronunciations: entry[1].map(([value, variety]) => variety ? { value, variety } : { value }),
    senses: entry[2].map(([id, partOfSpeech, definition, examples, synonyms]) => ({
      id,
      partOfSpeech,
      definition,
      examples,
      synonyms,
    })),
    source: "Open English WordNet 2025",
    senseStatus: "dictionary",
  };
}

function loadShard(shard: string) {
  const existing = shardCache.get(shard);
  if (existing) return existing;
  const request = fetch(`/dictionary/v1/${shard}.json`, { cache: "force-cache" })
    .then(async (response) => response.ok ? await response.json() as DictionaryShard : null)
    .catch(() => null);
  shardCache.set(shard, request);
  return request;
}
